import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { usePostHog } from "@posthog/react";
import { DEFAULT_INTERVAL, parseInterval } from "../config/plans";
import { hostThemeTokens, readStoredTheme, themeTokens } from "../theme";
import { ProPlans } from "../components/pro-plans";
import logo from "../assets/copresent-logo.svg";
import { UPGRADE_PAGE_VIEWED } from "../analytics/events";
import { PRIVACY_POLICY_URL } from "../config/legal";

// /upgrade — where the lifecycle emails (onboarding, cancelled, purchase),
// Stripe's cancel button and the billing portal's "return" all land.
//
// Deliberately the SAME card as the host controller's paywall overlay, in the
// host controller's theme: someone arriving from an email should recognise the
// thing they saw in the controller, not a second, differently-styled shop.

const Page = styled.main<{ $dark: boolean }>`
  ${(p) => themeTokens(p.$dark)}
  ${(p) => hostThemeTokens(p.$dark)}
  min-height: 100dvh;
  box-sizing: border-box;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 28px 16px 48px;
  background: var(--bg-page);
  color: var(--text-primary);
`;

const Brand = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  font-weight: 700;
  font-size: 16px;
  color: var(--text-headline);
  text-decoration: none;
  img {
    width: 32px;
    height: 32px;
  }
`;

const Lead = styled.p`
  max-width: 460px;
  margin: 22px 0 20px;
  text-align: center;
  font-size: 15px;
  line-height: 1.55;
  color: var(--text-secondary);
`;

const Footer = styled.p`
  max-width: 460px;
  margin: 20px 0 0;
  text-align: center;
  font-size: 13px;
  line-height: 1.6;
  color: var(--text-muted);
  a {
    color: var(--text-muted);
  }
`;

const params = () => new URLSearchParams(window.location.search);
const SOURCES = ["trial_email", "onboarding_email", "controller", "cancel_email", "purchase_email"];
const readSource = () => {
  const value = params().get("source");
  // controller_<paywall> comes back from Stripe's cancel button.
  return value && (SOURCES.includes(value) || /^controller_[a-z_]{1,28}$/.test(value)) ? value : "upgrade_page";
};

const Upgrade = () => {
  const posthog = usePostHog();
  // Read once: no toggle here, and the controller's choice is what the host
  // was last looking at.
  const [dark] = useState(() => readStoredTheme() === "dark");
  const [source] = useState(readSource);
  const [initialInterval] = useState(() => parseInterval(params().get("interval")) ?? DEFAULT_INTERVAL);
  const awaitingPayment = params().get("upgraded") === "1";
  const canceled = params().get("canceled") === "1";

  useEffect(() => {
    posthog?.capture(UPGRADE_PAGE_VIEWED, { source, canceled });
  }, [posthog, source, canceled]);

  return (
    <Page $dark={dark}>
      <Brand href="/"><img src={logo} alt="" />Copresent</Brand>
      <Lead>Present together. Give your copresenters control, keep your slides close, and cancel any time.</Lead>
      <ProPlans
        variant="page"
        source={source}
        initialInterval={initialInterval}
        awaitingPayment={awaitingPayment}
        canceled={canceled}
        signInReturnPath={(interval) => `/upgrade?source=${source}&interval=${interval}`}
      />
      <Footer>
        Only the host needs Pro. Everyone they invite joins for free.
        <br />
        <a href="/">Back to Copresent</a> · <a href={PRIVACY_POLICY_URL}>Privacy</a>
      </Footer>
    </Page>
  );
};

export default Upgrade;
